<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Billing Plans</a></li>
                    </ul>

                    <h3>Manage Billing Plans</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewBillingPlan" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Billing Plan</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="billingPlans"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No billing plans found.
                        </template>

                        <template #loading>
                            Loading billing plans. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'healthInsuranceSubmission'" :field="col.field"
                                    :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Health Insurance Submission Method</span>
                                    <span v-if="data.healthInsuranceSubmission === 'E'"
                                          :class="'submission-badge status-' + data.healthInsuranceSubmission">Email</span>
                                    <span v-if="data.healthInsuranceSubmission === 'D'"
                                          :class="'submission-badge status-' + data.healthInsuranceSubmission">Electronic Data Interchange (EDI)</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'useSystemProcedureRates'" :field="col.field"
                                    :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Use System Procedure Rates?</span>
                                    <span v-if="data.useSystemProcedureRates === 'Y'"
                                          :class="'confirmation-badge status-' + data.useSystemProcedureRates">Yes</span>
                                    <span v-if="data.useSystemProcedureRates === 'N'"
                                          :class="'confirmation-badge status-' + data.useSystemProcedureRates">No</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'useSystemProductRates'" :field="col.field"
                                    :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Use System Product Rates?</span>
                                    <span v-if="data.useSystemProductRates === 'Y'"
                                          :class="'confirmation-badge status-' + data.useSystemProductRates">Yes</span>
                                    <span v-if="data.useSystemProductRates === 'N'"
                                          :class="'confirmation-badge status-' + data.useSystemProductRates">No</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'availableForBookings'" :field="col.field"
                                    :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Available For Bookings?</span>
                                    <span v-if="data.availableForBookings === 'Y'"
                                          :class="'confirmation-badge status-' + data.availableForBookings">Yes</span>
                                    <span v-if="data.availableForBookings === 'N'"
                                          :class="'confirmation-badge status-' + data.availableForBookings">No</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'recurring'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Recurring?</span>
                                    <span v-if="data.recurring === 'Y'"
                                          :class="'confirmation-badge status-' + data.recurring">Yes</span>
                                    <span v-if="data.recurring === 'N'"
                                          :class="'confirmation-badge status-' + data.recurring">No</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'recurringType'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Recurring Type</span>
                                    <span v-if="data.recurringType === 'Y'"
                                          :class="'confirmation-badge status-' + data.recurringType">Yes</span>
                                    <span v-if="data.recurringType === 'N'"
                                          :class="'confirmation-badge status-' + data.recurringType">No</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Billing Plan {{ this.billingPlan.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="billingPlan.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="billingPlan.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="duration">Duration (Minutes)</label>
                        <InputNumber id="horizontal" v-model="billingPlan.duration" showButtons
                                     buttonLayout="horizontal"
                                     :step="5"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="priority">Priority</label>
                        <InputNumber id="horizontal" v-model="billingPlan.priority" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="availableForBookings">Available For Bookings?</label>
                        <Dropdown v-model="billingPlan.availableForBookings" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Available for Bookings"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="submissionOption">Health Insurance Submission Method</label>
                        <Dropdown v-model="billingPlan.healthInsuranceSubmission"
                                  :options="healthInsuranceSubmissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="useProcedureRates">Use System Procedure Rates?</label>
                        <Dropdown v-model="billingPlan.useSystemProcedureRates" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="useProductRates">Use System Product Rates?</label>
                        <Dropdown v-model="billingPlan.useSystemProductRates" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="recurring">Recurring?</label>
                        <Dropdown v-model="billingPlan.recurring" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Recurring"/>
                    </div>

                    <div class="field">
                        <label for="recurringType">Recurring Type?</label>
                        <Dropdown v-model="billingPlan.recurringType" :options="recurringTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Recurring Types"/>
                    </div>

                    <div class="field">
                        <label for="recurringDay">Recurring Day?</label>
                        <InputNumber id="horizontal" v-model="billingPlan.recurringDay" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="31"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="billingPlan.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateBillingPlan"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Billing Plan {{ this.billingPlan.name }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Billing Plan?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteBillingPlan"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Billing Plan</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newBillingPlan.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newBillingPlan.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="duration">Duration (Minutes)</label>
                        <InputNumber id="horizontal" v-model="newBillingPlan.duration" showButtons
                                     buttonLayout="horizontal"
                                     :step="5"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="priority">Priority</label>
                        <InputNumber id="horizontal" v-model="newBillingPlan.priority" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="availableForBookings">Available For Bookings?</label>
                        <Dropdown v-model="newBillingPlan.availableForBookings" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Available for Bookings"/>
                    </div>


                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="submissionOption">Health Insurance Submission Method</label>
                        <Dropdown v-model="newBillingPlan.healthInsuranceSubmission"
                                  :options="healthInsuranceSubmissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="useProcedureRates">Use System Procedure Rates?</label>
                        <Dropdown v-model="newBillingPlan.useSystemProcedureRates"
                                  :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="useProductRates">Use System Product Rates?</label>
                        <Dropdown v-model="newBillingPlan.useSystemProductRates"
                                  :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="recurring">Recurring?</label>
                        <Dropdown v-model="newBillingPlan.recurring" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Recurring"/>
                    </div>

                    <div class="field">
                        <label for="recurringType">Recurring Type?</label>
                        <Dropdown v-model="newBillingPlan.recurringType" :options="recurringTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Recurring Types"/>
                    </div>

                    <div class="field">
                        <label for="recurringDay">Recurring Day?</label>
                        <InputNumber id="horizontal" v-model="newBillingPlan.recurringDay" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="31"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createBillingPlan"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Billing Plan Components for {{ this.billingPlan.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callViewBillingPlans" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callNewBillingPlanComponent" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Billing Plan Component</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callHelp" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-question-circle mr-2"></i>
                                    <span>Help</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callSupport" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-info-circle mr-2"></i>
                                    <span>Contact Support</span>
                                </a>
                            </li>
                        </ul>
                    </div>


                    <DataTable
                        :value="billingPlanComponents"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No billing plan components found.
                        </template>

                        <template #loading>
                            Loading billing plan components. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItemsComponents(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <Column field="quantity" header="Quantity" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Quantity</span>
                                {{ data.quantity }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration.type==='H'" field="bhfCode" header="BHF Code" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">BHF Code</span>
                                {{ data.bhfCode }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration.type==='H'" field="nappiCode" header="NAPPI Code" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">NAPPI Code</span>
                                {{ data.nappiCode }}
                            </template>
                        </Column>

                        <Column v-show="companyConfiguration.type==='H'" field="modifier" header="Modifier" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Modifier</span>
                                {{ data.modifier }}
                            </template>
                        </Column>

                        <Column field="unitCost" header="Unit Cost" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Unit Cost</span>
                                {{ formatAmount(data.unitCost) }}
                            </template>
                        </Column>

                        <Column field="discount" header="Discount (%)" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Discount (%)</span>
                                {{ formatAsPercentage(data.discount) }}
                            </template>
                        </Column>

                        <Column field="amount" header="Amount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Amount</span>
                                {{ formatAmount(data.amount) }}
                            </template>
                        </Column>

                        <Column field="description" header="Description" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Description</span>
                                {{ data.description }}
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Component for Billing Plan {{ this.billingPlan.name }}</h3>

                    <div class="field">
                        <label for="quantity">Quantity</label>
                        <InputText id="quantity" v-model="newBillingPlanComponent.quantity"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type==='H' && companyConfiguration.region==='ZA'">
                        <label for="description">Description</label>
                        <AutoComplete
                            placeholder="Search by BHF Code or Description"
                            v-model="selectedComponent"
                            :suggestions="filteredCodes"
                            @complete="onCompleteNewComponent"
                            @item-select="onComponentSelectNewComponent"
                            field="label"
                            :minLength="2"
                            :completeOnFocus="false"/>

                    </div>

                    <div class="field" v-if="companyConfiguration.type!=='H' || companyConfiguration.region!=='ZA'">
                        <label for="description">Description</label>
                        <InputText id="description" v-model="newBillingPlanComponent.description"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type==='H' && companyConfiguration.region==='ZA'">
                        <label for="bhfCode">BHF Code</label>
                        <InputText id="bhfCode" v-model="newBillingPlanComponent.bhfCode"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type!=='H' || companyConfiguration.region!=='ZA'">
                        <label for="bhfCode">BHF Code</label>
                        <InputText id="bhfCode" v-model="newBillingPlanComponent.bhfCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="nappiCode">NAPPI Code</label>
                        <InputText id="nappiCode" v-model="newBillingPlanComponent.nappiCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="modifier">Modifier</label>
                        <InputText id="modifier" v-model="newBillingPlanComponent.modifier"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="unitCost">Unit Cost</label>
                        <InputText id="unitCost" v-model="newBillingPlanComponent.unitCost"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="discount">Discount (%)</label>
                        <InputNumber id="discount" v-model="newBillingPlanComponent.discount"
                                     :minFractionDigits="2" :maxFractionDigits="5" :min="0" :max="100"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createBillingPlanComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Billing Plan Component for {{ this.billingPlanComponent.description }}</h3>

                    <div class="field">
                        <label for="quantity">Quantity</label>
                        <InputText id="quantity" v-model.trim="billingPlanComponent.quantity"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type==='H' && companyConfiguration.region==='ZA'">
                        <label for="description">Description</label>
                        <AutoComplete
                            placeholder="Search by BHF Code or Description"
                            v-model="billingPlanComponent.description"
                            :suggestions="filteredCodes"
                            @complete="onCompleteExistingComponent"
                            @item-select="onComponentSelectExistingComponent"
                            field="label"
                            :minLength="2"
                            :completeOnFocus="false"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type!=='H' || companyConfiguration.region!=='ZA'">
                        <label for="description">Description</label>
                        <InputText id="description" v-model="billingPlanComponent.description"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type==='H' && companyConfiguration.region==='ZA'">
                        <label for="bhfCode">BHF Code</label>
                        <InputText id="bhfCode" v-model="billingPlanComponent.bhfCode"
                                   required="true"/>
                    </div>

                    <div class="field" v-if="companyConfiguration.type!=='H' || companyConfiguration.region!=='ZA'">
                        <label for="bhfCode">BHF Code</label>
                        <InputText id="bhfCode" v-model="billingPlanComponent.bhfCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="nappiCode">NAPPI Code</label>
                        <InputText id="nappiCode" v-model.trim="billingPlanComponent.nappiCode"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="modifier">Modifier</label>
                        <InputText id="modifier" v-model.trim="billingPlanComponent.modifier"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="unitCost">Unit Cost</label>
                        <InputText id="unitCost" v-model.trim="billingPlanComponent.unitCost"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="discount">Discount (%)</label>
                        <InputText id="unitCost" v-model.trim="billingPlanComponent.discount"
                                   required="true"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                    </div>

                    <h3 v-show="companyConfiguration.type==='H'">Diagnostic Codes</h3>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="icd10Code1">ICD10 Code (Primary)</label>
                        <AutoComplete v-model="billingPlanComponent.diagnosisCode1"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="icd10Code2">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="billingPlanComponent.diagnosisCode2"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="icd10Code3">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="billingPlanComponent.diagnosisCode3"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="icd10Code4">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="billingPlanComponent.diagnosisCode4"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <div v-show="companyConfiguration.type==='H'" class="field">
                        <label for="icd10Code5">ICD10 Code (Supplementary)</label>
                        <AutoComplete v-model="billingPlanComponent.diagnosisCode5"
                                      :suggestions="filteredDiagnosticCodes"
                                      @complete="searchDiagnosticCodes($event)"
                                      field="icd10CodeLongDescription"
                                      placeholder="Type the first few characters to search"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateBillingPlanComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Billing Plan Component for {{ this.billingPlanComponent.description }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Billing Plan Component?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteBillingPlanComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import BillingPlanService from "@/service/BillingPlanService";
import BillingPlanComponentService from "@/service/BillingPlanComponentService";
import numeral from "numeral";
import Icd10CodeService from "@/service/Icd10CodeService";
import ProviderComponentService from "@/service/ProviderComponentService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
        this.icd10CodeService = new Icd10CodeService();

    },
    async mounted() {
        await this.loadCompanyContext();

        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[PmOverviewDashboard][mounted] System is active.");
            console.log("[PmOverviewDashboard][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] System is NOT active.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[PmOverviewDashboard][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filteredCodes: [],
            selectedComponent: null,
            isEditingComponent: false,
            selectedUpdateComponent: null,
            debounceId: null,
            abortCtrl: null,
            requestSeq: 0,
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            filteredItemsComponentsCache: new Map(),
            billingPlan: {},
            newBillingPlan: {},
            billingPlans: null,
            originalBillingPlans: null,
            billingPlanComponents: null,
            billingPlanComponent: {},
            newBillingPlanComponent: {},
            filteredDiagnosticCodes: [],
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            recurringTypes: [
                {name: 'None', code: 'N'},
                {name: 'Daily', code: 'D'},
                {name: 'Weekly', code: 'W'},
                {name: 'Bi-Weekly', code: 'B'},
                {name: 'Monthly', code: 'M'}
            ]
        }
    },
    watch: {
        billingPlans() {
            this.filteredItemsCache.clear();
        },
        billingPlanComponents() {
            this.filteredItemsComponentsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                const [userConfigurationData, companyConfigurationData, billingPlansData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    BillingPlanService.getBillingPlans(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.billingPlans = billingPlansData.data;
                this.originalBillingPlans = [...this.billingPlans];

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.billingPlans = ", this.billingPlans);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);

            if (this.companyConfiguration.type==='H') {
                this.columnsDefault = [
                    {field: 'name', header: 'Name', sortable: true},
                    {field: 'description', header: 'Description', sortable: true},
                    {field: 'status', header: 'Status', sortable: true},
                    {field: 'duration', header: 'Duration', sortable: true},
                    {field: 'priority', header: 'Priority', sortable: true},
                    {field: 'healthInsuranceSubmission', header: 'Health Insurance Submission Method', sortable: true},
                    {field: 'useSystemProcedureRates', header: 'Use System Procedure Rates', sortable: true},
                    {field: 'useSystemProductRates', header: 'Use System Product Rates', sortable: true}
                ];

                this.columnsFullList = [
                    {field: 'id', header: 'ID', sortable: true},
                    {field: 'name', header: 'Name', sortable: true},
                    {field: 'description', header: 'Description', sortable: true},
                    {field: 'status', header: 'Status', sortable: true},
                    {field: 'duration', header: 'Duration', sortable: true},
                    {field: 'priority', header: 'Priority', sortable: true},
                    {field: 'healthInsuranceSubmission', header: 'Health Insurance Submission Method', sortable: true},
                    {field: 'useSystemProcedureRates', header: 'Use System Procedure Rates', sortable: true},
                    {field: 'useSystemProductRates', header: 'Use System Product Rates', sortable: true},
                    {field: 'availableForBookings', header: 'Available For Bookings?', sortable: true},
                    {field: 'recurring', header: 'Recurring?', sortable: true},
                    {field: 'recurringType', header: 'Recurring Type', sortable: true},
                    {field: 'recurringDay', header: 'Recurring Day', sortable: true}
                ];

            } else {
                this.columnsDefault = [
                    {field: 'name', header: 'Name', sortable: true},
                    {field: 'description', header: 'Description', sortable: true},
                    {field: 'status', header: 'Status', sortable: true},
                    {field: 'duration', header: 'Duration', sortable: true},
                    {field: 'priority', header: 'Priority', sortable: true}
                ];

                this.columnsFullList = [
                    {field: 'id', header: 'ID', sortable: true},
                    {field: 'name', header: 'Name', sortable: true},
                    {field: 'description', header: 'Description', sortable: true},
                    {field: 'status', header: 'Status', sortable: true},
                    {field: 'duration', header: 'Duration', sortable: true},
                    {field: 'priority', header: 'Priority', sortable: true},
                    {field: 'availableForBookings', header: 'Available For Bookings?', sortable: true},
                    {field: 'recurring', header: 'Recurring?', sortable: true},
                    {field: 'recurringType', header: 'Recurring Type', sortable: true},
                    {field: 'recurringDay', header: 'Recurring Day', sortable: true}
                ];
            }

            this.selectedColumns = this.columnsDefault;

            this.standardFilters = [
                {field: 'active', header: 'Active'},
                {field: 'inactive', header: 'Inactive'}
            ];
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewBillingPlan() {
            this.newBillingPlan = {};
            this.newBillingPlan.duration = 60;
            this.newBillingPlan.priority = 1;
            this.newBillingPlan.healthInsuranceSubmission = 'E';
            this.newBillingPlan.useSystemProcedureRates = 'N';
            this.newBillingPlan.useSystemProductRates = 'N';
            this.newBillingPlan.availableForBookings = 'N';
            this.newBillingPlan.recurring = 'N';
            this.newBillingPlan.recurringType = 'N';
            this.newBillingPlan.recurringDay = 0;
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callUpdateBillingPlan(data) {
            this.activeTab = 2;
            this.billingPlan = data;
        },
        callDeleteBillingPlan(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.billingPlan = data;
        },
        callNewBillingPlanComponent() {
            this.newBillingPlanComponent = {};
            this.newBillingPlanComponent.quantity = 1
            this.newBillingPlanComponent.unitCost = 0;
            this.newBillingPlanComponent.amount = 0;
            this.newBillingPlanComponent.discount = 0.0;
            this.newBillingPlanComponent.description = null;
            this.newBillingPlanComponent.bhfCode = null;
            this.newBillingPlanComponent.nappiCode = null;
            this.selectedComponent = null;
            this.activeTab = 6;
        },
        callUpdateBillingPlanComponent(data) {
            this.activeTab = 7;
            this.billingPlanComponent = data;
        },
        callDeleteBillingPlanComponent(data) {
            this.activeTab = 8;
            this.confirmationResponse = 'N';
            this.billingPlanComponent = data;
        },
        async callViewBillingPlans() {
            this.activeTab = 1;
        },
        async callViewBillingPlanComponents(data) {
            this.billingPlan = data;
            this.activeTab = 5;

            const [billingPlanComponentData] = await Promise.all([
                BillingPlanComponentService.getBillingPlanComponentsForBillingPlan(this.billingPlan.id)
            ]);

            this.billingPlanComponents = billingPlanComponentData.data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.billingPlans !== null) {

                if (isActive) {
                    this.billingPlans = this.originalBillingPlans.filter(billingPlan => billingPlan.status === 'A');
                } else if (isInactive) {
                    this.billingPlans = this.originalBillingPlans.filter(billingPlan => billingPlan.status === 'I');
                } else {
                    this.billingPlans = this.originalBillingPlans;
                }

            } else {
                // Handle the case when auditTrails is null
                this.billingPlans = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.clearFilter();
                    this.callUpdateBillingPlan(data);
                }
            });

            items.push({
                label: 'View Components',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.clearFilter();
                    this.callViewBillingPlanComponents(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.clearFilter();
                    this.callDeleteBillingPlan(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        getFilteredItemsComponents(data) {
            if (this.filteredItemsComponentsCache.has(data.id)) {
                return this.filteredItemsComponentsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.clearFilter();
                    this.callUpdateBillingPlanComponent(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.clearFilter();
                    this.callDeleteBillingPlanComponent(data);
                }
            });

            this.filteredItemsComponentsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            }
        },
        searchDiagnosticCodes(event) {
            console.log("[" + name + "][searchDiagnosticCodes] this.filteredDiagnosticCodes BEFORE = ", this.filteredDiagnosticCodes);
            console.log("[" + name + "][searchDiagnosticCodes] event.query.toLowerCase() = ", event.query.toLowerCase());

            this.icd10CodeService.getIcd10CodesSimple(event.query.toLowerCase())
                .then(data => {
                    this.filteredDiagnosticCodes = data.data;
                })
                .catch(e => {
                    console.log(e);
                });

            console.log("[" + name + "][searchDiagnosticCodes] this.filteredIcd10Codes AFTER = ", this.filteredDiagnosticCodes);

        },
        formatAsPercentage(number) {
            // Check if the input is a valid number
            if (isNaN(number)) {
                return "Invalid Number";
            }

            // Divide the number by 100 to convert it to a percentage
            const percentage = number / 100;

            // Use toLocaleString to format as a percentage with two decimal places
            return percentage.toLocaleString(undefined, {
                style: "percent",
                minimumFractionDigits: 2,
                maximumFractionDigits: 5,
            });
        },
        onCompleteNewComponent(event) {
            console.log('[ConfigurationBilling][onCompleteNewComponent] event = ', event);

            const q = (event?.query || '').trim();

            if (q.length < 2) {
                this.filteredCodes = [];
                this.cancelPending();
                return;
            }

            // Debounce 300 ms
            clearTimeout(this.debounceId);
            this.debounceId = setTimeout(() => this.doSearch(q), 300);
        },
        onCompleteExistingComponent(event) {
            console.log('[ConfigurationBilling][onCompleteExistingComponent] event = ', event);

            const q = (event?.query || '').trim();

            if (q.length < 2) {
                this.filteredCodes = [];
                this.cancelPending();
                return;
            }

            // Debounce 300 ms
            clearTimeout(this.debounceId);
            this.debounceId = setTimeout(() => this.doSearch(q), 300);
        },
        cancelPending() {
            if (this.abortCtrl) {
                this.abortCtrl.abort();
                this.abortCtrl = null;
            }
        },
        doSearch(query) {
            console.log('[ConfigurationBilling][doSearch] query = ', query);

            this.cancelPending();

            this.abortCtrl = new AbortController();

            const seq = ++this.requestSeq;

            ProviderComponentService.searchProviderComponentsByDescription(
                query.toLowerCase(),
                {signal: this.abortCtrl.signal} // axios supports AbortController
            )
                .then((res) => {
                    if (seq !== this.requestSeq) return; // ignore stale

                    const list = res.data || [];

                    this.filteredCodes = list.map(component => ({
                        label: `${component.code} (${component.description})`,
                        code: component.code,
                        description: component.description
                    }));
                })
                .catch((e) => {
                    if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
                })
                .finally(() => {
                    this.abortCtrl = null;
                });
        },
        onComponentSelectNewComponent(event) {
            console.log('[ConfigurationBilling][onComponentSelectNewComponent] event = ', event);

            const selected = event.value;

            if (!selected) return;

            this.newBillingPlanComponent.description = selected.description;
            this.newBillingPlanComponent.bhfCode = selected.code;
        },
        onComponentSelectExistingComponent(event) {
            console.log('[ConfigurationBilling][onComponentSelectExistingComponent] event = ', event);

            const selected = event.value;

            if (!selected) return;

            this.billingPlanComponent.description = selected.description;
            this.billingPlanComponent.bhfCode = selected.code;

            console.log('[ConfigurationBilling][onComponentSelectExistingComponent] ' +
                'this.billingPlanComponent.description = ', this.billingPlanComponent.description);
            console.log('[ConfigurationBilling][onComponentSelectExistingComponent] ' +
                'this.billingPlanComponent.bhfCode = ', this.billingPlanComponent.bhfCode);
        },
        async createBillingPlan() {
            this.submitted = true;

            if (this.newBillingPlan.name) {

                try {
                    var billingPlanDto = {
                        name: this.newBillingPlan.name,
                        description: this.newBillingPlan.description,
                        duration: this.newBillingPlan.duration,
                        priority: this.newBillingPlan.priority,
                        availableForBookings: this.newBillingPlan.availableForBookings,
                        recurring: this.newBillingPlan.recurring,
                        recurringType: this.newBillingPlan.recurringType,
                        recurringDay: this.newBillingPlan.recurringDay,
                        healthInsuranceSubmission: this.newBillingPlan.healthInsuranceSubmission,
                        useSystemProcedureRates: this.newBillingPlan.useSystemProcedureRates,
                        useSystemProductRates: this.newBillingPlan.useSystemProductRates,
                        status: 'A'
                    };

                    const response = await BillingPlanService.createBillingPlan(this.profile.companyId, billingPlanDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [billingPlanData] = await Promise.all([
                        BillingPlanService.getBillingPlans(this.profile.companyId)
                    ]);

                    this.billingPlans = billingPlanData.data;
                    this.originalBillingPlans = billingPlanData.data;
                    this.userStore.setBillingPlans(billingPlanData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created billing plan',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create billing plan. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateBillingPlan() {
            this.submitted = true;

            if (this.billingPlan.id) {

                try {
                    var billingPlanDto = {
                        name: this.billingPlan.name,
                        description: this.billingPlan.description,
                        duration: this.billingPlan.duration,
                        priority: this.billingPlan.priority,
                        availableForBookings: this.billingPlan.availableForBookings,
                        recurring: this.billingPlan.recurring,
                        recurringType: this.billingPlan.recurringType,
                        recurringDay: this.billingPlan.recurringDay,
                        healthInsuranceSubmission: this.billingPlan.healthInsuranceSubmission,
                        useSystemProcedureRates: this.billingPlan.useSystemProcedureRates,
                        useSystemProductRates: this.billingPlan.useSystemProductRates,
                        status: this.billingPlan.status
                    };

                    const response = await BillingPlanService.updateBillingPlan(this.billingPlan.id, billingPlanDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [billingPlanData] = await Promise.all([
                        BillingPlanService.getBillingPlans(this.profile.companyId)
                    ]);

                    this.billingPlans = billingPlanData.data;
                    this.originalBillingPlans = billingPlanData.data;
                    this.userStore.setBillingPlans(billingPlanData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated billing plan',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update billing plan. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteBillingPlan() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.billingPlan.id) {

                    try {
                        const response = await BillingPlanService.deleteBillingPlan(this.billingPlan.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [billingPlanData] = await Promise.all([
                            BillingPlanService.getBillingPlans(this.profile.companyId)
                        ]);

                        this.billingPlans = billingPlanData.data;
                        this.originalBillingPlans = billingPlanData.data;
                        this.userStore.setBillingPlans(billingPlanData.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted billing plan',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete billing plan. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createBillingPlanComponent() {
            this.submitted = true;

            if (this.newBillingPlanComponent.description) {

                try {
                    var billingPlanComponentDto = {
                        quantity: this.newBillingPlanComponent.quantity,
                        description: this.newBillingPlanComponent.description,
                        bhfCode: this.newBillingPlanComponent.bhfCode,
                        nappiCode: this.newBillingPlanComponent.nappiCode,
                        modifier: this.newBillingPlanComponent.modifier,
                        discount: this.newBillingPlanComponent.discount,
                        unitCost: this.newBillingPlanComponent.unitCost,
                        amount: this.newBillingPlanComponent.unitCost * this.newBillingPlanComponent.quantity * ((100 - this.newBillingPlanComponent.discount) / 100.0),
                        billingPlanId: this.billingPlan.id,
                        status: 'A'
                    };

                    const response = await BillingPlanComponentService.createBillingPlanComponent(this.billingPlan.id, billingPlanComponentDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [billingPlanComponentData] = await Promise.all([
                        BillingPlanComponentService.getBillingPlanComponentsForBillingPlan(this.billingPlan.id)
                    ]);

                    this.billingPlanComponents = billingPlanComponentData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created billing plan component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create billing plan component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newBillingPlanComponent = {};
            this.activeTab = 5;
            this.submitted = false;
        },
        async updateBillingPlanComponent() {
            this.submitted = true;

            if (this.billingPlanComponent.id) {

                try {
                    var tempDiagnosisCode1 = "";
                    var tempDiagnosisCode2 = "";
                    var tempDiagnosisCode3 = "";
                    var tempDiagnosisCode4 = "";
                    var tempDiagnosisCode5 = "";

                    if (this.billingPlanComponent.diagnosisCode1 != null) {
                        if (this.billingPlanComponent.diagnosisCode1.length > 1) {
                            tempDiagnosisCode1 = this.billingPlanComponent.diagnosisCode1;
                        } else {
                            try {
                                tempDiagnosisCode1 = this.billingPlanComponent.diagnosisCode1.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode1 = this.billingPlanComponent.diagnosisCode1;
                            }
                        }
                    }

                    if (this.billingPlanComponent.diagnosisCode2 != null) {
                        if (this.billingPlanComponent.diagnosisCode2.length > 1) {
                            tempDiagnosisCode2 = this.billingPlanComponent.diagnosisCode2;
                        } else {
                            try {
                                tempDiagnosisCode2 = this.billingPlanComponent.diagnosisCode2.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode2 = this.billingPlanComponent.diagnosisCode2;
                            }
                        }
                    }

                    if (this.billingPlanComponent.diagnosisCode3 != null) {
                        if (this.billingPlanComponent.diagnosisCode3.length > 1) {
                            tempDiagnosisCode3 = this.billingPlanComponent.diagnosisCode3;
                        } else {

                            try {
                                tempDiagnosisCode3 = this.billingPlanComponent.diagnosisCode3.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode3 = this.billingPlanComponent.diagnosisCode3;
                            }
                        }
                    }

                    if (this.billingPlanComponent.diagnosisCode4 != null) {
                        if (this.billingPlanComponent.diagnosisCode4.length > 1) {
                            tempDiagnosisCode4 = this.billingPlanComponent.diagnosisCode4;
                        } else {

                            try {
                                tempDiagnosisCode4 = this.billingPlanComponent.diagnosisCode4.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode4 = this.billingPlanComponent.diagnosisCode4;
                            }
                        }
                    }

                    if (this.billingPlanComponent.diagnosisCode5 != null) {
                        if (this.billingPlanComponent.diagnosisCode5.length > 1) {
                            tempDiagnosisCode5 = this.billingPlanComponent.diagnosisCode5;
                        } else {

                            try {
                                tempDiagnosisCode5 = this.billingPlanComponent.diagnosisCode5.icd10Code;
                            } catch (error) {
                                tempDiagnosisCode5 = this.billingPlanComponent.diagnosisCode5;
                            }
                        }
                    }

                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] tempDiagnosisCode1 = ", tempDiagnosisCode1);
                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] tempDiagnosisCode2 = ", tempDiagnosisCode2);
                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] tempDiagnosisCode3 = ", tempDiagnosisCode3);
                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] tempDiagnosisCode4 = ", tempDiagnosisCode4);
                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] tempDiagnosisCode5 = ", tempDiagnosisCode5);

                    var editBillingPlanComponentDto = {
                        id: this.billingPlanComponent.id,
                        diagnosisCode1: tempDiagnosisCode1,
                        diagnosisCode2: tempDiagnosisCode2,
                        diagnosisCode3: tempDiagnosisCode3,
                        diagnosisCode4: tempDiagnosisCode4,
                        diagnosisCode5: tempDiagnosisCode5,
                        billingPlanId: this.billingPlanComponent.billingPlanId,
                        quantity: this.billingPlanComponent.quantity,
                        description: this.billingPlanComponent.description,
                        bhfCode: this.billingPlanComponent.bhfCode,
                        nappiCode: this.billingPlanComponent.nappiCode,
                        modifier: this.billingPlanComponent.modifier,
                        discount: this.billingPlanComponent.discount,
                        unitCost: this.billingPlanComponent.unitCost,
                        amount: this.billingPlanComponent.unitCost * this.billingPlanComponent.quantity * ((100 - this.billingPlanComponent.discount) / 100.0),
                        status: this.billingPlanComponent.status,
                    };

                    console.log("[MsConfigurationBilling][updateBillingPlanComponent] editBillingPlanComponentDto = ", editBillingPlanComponentDto);

                    const response = await BillingPlanComponentService.updateBillingPlanComponent(this.billingPlanComponent.id, editBillingPlanComponentDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [billingPlanComponentData] = await Promise.all([
                        BillingPlanComponentService.getBillingPlanComponentsForBillingPlan(this.billingPlan.id)
                    ]);

                    this.billingPlanComponents = billingPlanComponentData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated billing plan component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update billing plan component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async deleteBillingPlanComponent() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.billingPlanComponent.id) {

                    try {
                        const response = await BillingPlanComponentService.deleteBillingPlanComponent(this.billingPlanComponent.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [billingPlanComponentData] = await Promise.all([
                            BillingPlanComponentService.getBillingPlanComponentsForBillingPlan(this.billingPlan.id)
                        ]);

                        this.billingPlanComponents = billingPlanComponentData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted billing plan component',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete billing plan component. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 5;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .confirmation-badge, .submission-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.confirmation-badge {
    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }
}

.submission-badge {
    &.status-E {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-D {
        background: #FFCDD2;
        color: #C63737;
    }

}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
